/**
 * Client helper for the live Google reviews aggregate.
 *
 * Calls the gsl-server `/google/reviews` route (the Maps API key stays on the
 * server). By default it targets the same backend as the Instagram feed:
 *   prod → /api/google/reviews    (same-origin; Vercel proxies to gsl-server, see vercel.json)
 *   dev  → http://localhost:5000/api/google/reviews   (run `npm run launch` in gsl-server)
 *
 * Override with `VITE_GOOGLE_REVIEWS_URL` (e.g. `/api/google-reviews` to use the
 * standalone `server/google-reviews-server.mjs` dev proxy instead). Optional
 * `VITE_GOOGLE_REVIEWS_SECRET` is sent as the `X-Reviews-Secret` header.
 *
 * Returns `null` if the request fails, so the UI falls back to curated content.
 */

export interface GoogleReview {
    readonly rating: number;
    readonly text: string;
    readonly authorName: string;
    readonly authorPhoto: string;
    readonly authorUri: string;
    readonly relativeTime: string;
    readonly publishTime: string;
}

export interface GoogleReviewsResult {
    readonly rating: number;
    readonly total: number;
    readonly googleMapsUri: string;
    readonly reviews: readonly GoogleReview[];
}

const STORAGE_KEY = 'gsl_google_reviews_v1';
const TTL_MS = 6 * 60 * 60 * 1000;

function readCache(): GoogleReviewsResult | null {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as { at: number; data: GoogleReviewsResult };
        if (Date.now() - parsed.at > TTL_MS) return null;
        return parsed.data;
    } catch {
        return null;
    }
}

function writeCache(data: GoogleReviewsResult): void {
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ at: Date.now(), data }));
    } catch {
        /* private mode */
    }
}

/** Minimum characters of review text required to be worth featuring (filters out rating-only reviews). */
export const MIN_REVIEW_LENGTH = 100;

/**
 * 5-star reviews with enough written content to be useful, most recent first.
 * Filters out rating-only reviews and very short blurbs (< {@link MIN_REVIEW_LENGTH} chars).
 */
export function qualifyingReviews(result: GoogleReviewsResult | null): readonly GoogleReview[] {
    if (!result) return [];
    return [...result.reviews]
        .filter((r) => r.rating >= 5 && r.text.trim().length >= MIN_REVIEW_LENGTH)
        .sort((a, b) => (b.publishTime || '').localeCompare(a.publishTime || ''));
}

function resolveEndpoint(): string {
    const override = import.meta.env.VITE_GOOGLE_REVIEWS_URL?.trim();
    if (override) return override;
    const base = import.meta.env.PROD ? '/api/' : 'http://localhost:5000/api/';
    return `${base}google/reviews`;
}

export async function fetchGoogleReviews(signal?: AbortSignal): Promise<GoogleReviewsResult | null> {
    const cached = readCache();
    if (cached) return cached;

    const endpoint = resolveEndpoint();
    const secret = import.meta.env.VITE_GOOGLE_REVIEWS_SECRET?.trim();

    try {
        const headers: Record<string, string> = {};
        if (secret) headers['X-Reviews-Secret'] = secret;

        const res = await fetch(endpoint, { headers, signal });
        if (!res.ok) throw new Error(`google-reviews ${res.status}`);

        const data = (await res.json()) as Partial<GoogleReviewsResult>;
        if (typeof data.rating !== 'number') return null;

        const result: GoogleReviewsResult = {
            rating: data.rating,
            total: data.total ?? 0,
            googleMapsUri: data.googleMapsUri ?? '',
            reviews: Array.isArray(data.reviews) ? data.reviews : [],
        };
        writeCache(result);
        return result;
    } catch {
        return null;
    }
}
